import SiteLayout from '@/components/feature/SiteLayout';
import PageIntro from '@/components/feature/PageIntro';
import Reveal from '@/components/base/Reveal';
import { Link } from 'react-router-dom';
import { aboutImages } from '@/mocks/site';
import useDocumentMeta from '@/hooks/useDocumentMeta';

const philosophy = [
  { title: 'Diagnose before replacing', text: 'Parts are only fitted when testing proves they are the cause. It saves money and fixes problems the first time.' },
  { title: 'Explain in plain language', text: 'You see what we see — photos, readings and worn parts — with clear priorities on what matters now.' },
  { title: 'Treat every car as our own', text: 'Seat and floor covers, careful handling and a clean handover are standard, not an upgrade.' },
];

const standards = [
  'Manufacturer repair procedures and torque specifications',
  'Quality parts, with options clearly explained',
  'Additional check on safety-critical work',
  'Final road test before every handover',
  'Written record of all work completed',
];

export default function AboutPage() {
  useDocumentMeta('About Our Auto Repair Workshop | Redline Garage', 'Learn how Redline Garage approaches auto repair: diagnose before replacing, explain work in plain language and follow manufacturer procedures on every job.');

  return (
    <SiteLayout>
      <PageIntro
        title="About the workshop"
        text="An independent workshop built around one idea: careful, well-explained work that drivers can trust."
        image={aboutImages.intro}
        alt="Professional auto repair workshop interior with cars on lifts"
      />

      <section className="py-16 md:py-[104px]">
        <div className="mx-auto max-w-[1280px] px-4 md:px-8 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-6 items-center">
          <Reveal className="lg:col-span-6">
            <div className="relative h-[300px] sm:h-[420px] lg:h-[480px] cut-tr cut-tr-lg overflow-hidden bg-background-200">
              <img src={aboutImages.workshop} alt="Organized tool wall in a professional workshop" title="Redline Garage workshop" className="reveal-zoom w-full h-full object-cover object-center" />
            </div>
          </Reveal>
          <Reveal className="lg:col-span-5 lg:col-start-8" delay={100}>
            <h2 className="font-heading uppercase text-[36px] md:text-[48px] leading-[0.95]">A workshop run with precision</h2>
            <p className="mt-5 text-base text-foreground-700 leading-relaxed">Our service bays are organized for clean, efficient work: modern lifts, current diagnostic equipment and a tool system where everything has its place.</p>
            <p className="mt-4 text-base text-foreground-700 leading-relaxed">That order isn&apos;t cosmetic. It&apos;s how we keep jobs on schedule, avoid mistakes and give you an accurate picture of what your vehicle needs.</p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              {[['ri-car-line', 'All makes & models'], ['ri-scan-2-line', 'Modern diagnostic tools']].map(([icon, label]) => (
                <div key={label} className="flex items-center gap-3 bg-background-100 border border-background-300/60 rounded-lg p-4">
                  <span className="w-9 h-9 flex items-center justify-center text-primary-400 text-lg"><i className={icon} /></span>
                  <span className="text-sm text-foreground-900">{label}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-[104px] bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 md:px-8 lg:px-10">
          <Reveal><h2 className="font-heading uppercase text-[36px] md:text-[48px] leading-[0.95] mb-10 md:mb-12">Service philosophy</h2></Reveal>
          <div className="border-t border-background-300">
            {philosophy.map((p, i) => (
              <Reveal key={p.title} delay={i * 80}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-6 py-7 md:py-8 border-b border-background-300 group">
                  <span className="md:col-span-1 font-heading text-[20px] text-primary-400">0{i + 1}</span>
                  <h3 className="md:col-span-5 font-heading uppercase text-[24px] md:text-[28px] leading-tight group-hover:text-primary-400 transition-colors">{p.title}</h3>
                  <p className="md:col-span-6 text-base text-foreground-700 leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-[104px]">
        <div className="mx-auto max-w-[1280px] px-4 md:px-8 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-6 items-center">
          <Reveal className="lg:col-span-5 order-2 lg:order-1">
            <h2 className="font-heading uppercase text-[36px] md:text-[48px] leading-[0.95]">Quality standards</h2>
            <p className="mt-5 text-base text-foreground-700 leading-relaxed">The checklist every job follows, regardless of size.</p>
            <ul className="mt-8 space-y-3">
              {standards.map((s) => (
                <li key={s} className="flex items-start gap-3 bg-background-100 border border-background-300/60 rounded-md px-4 py-3.5 text-sm text-foreground-900">
                  <span className="w-5 h-5 mt-px flex items-center justify-center text-accent-500"><i className="ri-check-line" /></span>{s}
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <Link to="/contact" className="group/cta inline-flex items-center gap-4 min-h-[52px] px-6 border border-background-300/70 hover:border-primary-500/70 text-xs font-semibold uppercase tracking-[0.24em] text-foreground-950 whitespace-nowrap cursor-pointer transition-colors duration-300">
                Book Service
                <i className="ri-arrow-right-up-line text-base text-primary-500 transition-transform duration-200 group-hover/cta:translate-x-1 group-hover/cta:-translate-y-1" />
              </Link>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-6 lg:col-start-7 order-1 lg:order-2" delay={100}>
            <div className="relative h-[300px] sm:h-[420px] lg:h-[540px] cut-bl overflow-hidden bg-background-200">
              <img src={aboutImages.standards} alt="Technician using a red torque wrench" title="Quality workmanship — torque to specification" className="reveal-zoom w-full h-full object-cover object-center" />
            </div>
          </Reveal>
        </div>
      </section>
    </SiteLayout>
  );
}